import { AccessibilityHelpers } from 'js/components/Util/Accessibility.js';
const helpersInstance = new AccessibilityHelpers();
import { InputHelper } from 'ts/components/SearchForm/InputHelper.ts';
import { FormValueScratchpad } from 'js/components/Cobalt/Locator/FormValueScratchpad.js';
import createFocusTrap from 'focus-trap';

export function ensureFilterCountBadges() {
  for (const button of document.querySelectorAll('.js-filters-open')) {
    if (button.querySelector('.js-filters-count')) {
      continue;
    }

    const badge = document.createElement('span');
    badge.className = 'Locator-filtersCount js-filters-count';
    badge.setAttribute('aria-hidden', 'true');
    badge.style.display = 'none';

    const srOnly = button.querySelector('.sr-only');
    if (srOnly) {
      button.insertBefore(badge, srOnly);
    } else {
      button.appendChild(badge);
    }
  }
}

export function updateFilterCountBadge(count) {
  ensureFilterCountBadges();
  const counterEls = document.querySelectorAll('.js-filters-count');
  for (const counter of counterEls) {
    counter.innerHTML = count;
    counter.style.display = count > 0 ? 'flex' : 'none';
  }
}

export class FilterModal {
  constructor(filtersWrapper, triggerEl, searchForm) {
    if (!filtersWrapper) { return; };
    this.filtersWrapper = filtersWrapper;
    this.searchForm = searchForm;
    this.overlayEl = document.querySelector('.js-filters-overlay');
    this.inputs = Array.from(filtersWrapper.querySelectorAll('input,select')).
                        filter(el => el.name);

    if (triggerEl) {
      triggerEl.onclick = this.open.bind(this);
    }

    this.fvs = new FormValueScratchpad();
    this.fvs.registerInputs(this.inputs);

    this.filtersBound = false;
    this.bindFilters();
    this.updateFilterCount();
  };

  bindFilters() {
    if (this.filtersBound) { return; }
    this.filtersBound = true;

    for (const clearButton of this.filtersWrapper.querySelectorAll('.js-filters-clear')) {
      clearButton.addEventListener('click', this.clearAll.bind(this));
    }

    for (const toggleButton of document.querySelectorAll('.js-filters-open')) {
      toggleButton.onclick = this.open.bind(this);
    }

    for (const toggleButton of this.filtersWrapper.querySelectorAll('.js-filters-close')) {
      toggleButton.onclick = this.close.bind(this, true);
    }

    for (const toggleButton of this.filtersWrapper.querySelectorAll('.js-filters-apply')) {
      toggleButton.onclick = this.close.bind(this, false);
    }

    const focusTrapEl = this.filtersWrapper.querySelector('.js-filters-focus');
    if (focusTrapEl) {
      this.focusTrap = createFocusTrap(focusTrapEl);
    }

    for (const input of this.inputs) {
      input.addEventListener('change', () => {
        this.updateApplyButtonState();
        this.updateFilterCount();
      });
    }
  }

  updateApplyButtonState() {
    const appliers = this.filtersWrapper.querySelectorAll('.js-filters-apply');
    const disabled = this.fvs.changesAreCommitted();
    for (const toggleButton of appliers) {
      toggleButton.disabled = disabled;
    }
  }

  updateFilterCount() {
    updateFilterCountBadge(this.getActiveFiltersCount());
  }

  open() {
    this.fvs.commit();
    this.updateApplyButtonState();

    const header = document.querySelector('header');
    if (header) {
      header.classList.add('is-background');
    }

    document.body.classList.add('is-overflowHidden');
    this.filtersWrapper.classList.add('is-active');
    if (this.overlayEl) {
      this.overlayEl.classList.add('is-active');
    }

    if (this.focusTrap) {
      this.focusTrap.activate();
    }

    for (const controllingElement of this.filtersWrapper.querySelectorAll('js-filters-apply,.js-filters-close,.js-filters-open')) {
      helpersInstance.setAriaProp(controllingElement, 'expanded', true);
    }
  }

  close(restoreState) {
    if (restoreState) {
      this.fvs.reset();
    } else {
      this.fvs.commit();

      if (this.searchForm) {
        const query = this.searchForm.buildQuery();
        if (query) {
          window.location.href = `${this.searchForm.formElement.action}?${query}`;
        }
      }
    }
    this.updateFilterCount();

    this.filtersWrapper.classList.remove('is-active');

    const header = document.querySelector('header');
    if (header) {
      header.classList.remove('is-background');
    }

    document.body.classList.remove('is-overflowHidden');

    if (this.overlayEl) {
      this.overlayEl.classList.remove('is-active');
    }

    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }

    for (const controllingElement of this.filtersWrapper.querySelectorAll('js-filters-apply,.js-filters-close,.js-filters-open')) {
      helpersInstance.setAriaProp(controllingElement, 'expanded', false);
    }
  }

  getActiveFiltersCount() {
    return this.inputs.reduce((filterCount, input) => InputHelper.isEmpty(input) ? filterCount : filterCount + 1, 0);
  }

  clearAll() {
    for (const input of this.inputs) {
      InputHelper.reset(input);
    }

    this.updateApplyButtonState();
    this.updateFilterCount();
  }
}